<template>
  <div>
    <button
      :class="[
        `fixed z-50 rounded-full bg-${themeColor}-600 p-4 text-white shadow-lg transition-all duration-300
        hover:bg-${themeColor}-700 focus:outline-none focus:ring-2 focus:ring-${themeColor}-500
        focus:ring-offset-2`,
        isOpen ? 'left-64 sm:left-72' : 'left-4',
        'top-[95%] -translate-y-1/2 transform'
      ]"
      @click="toggleSidebar"
    >
      <ChevronLeft
        v-if="isOpen"
        class="h-6 w-6"
      />
      <Menu
        v-else
        class="h-6 w-6"
      />
    </button>
    <div
      :class="[
        `fixed left-0 top-0 h-full overflow-y-auto bg-gradient-to-b from-${themeColor}-100 to-white shadow-lg
        transition-all duration-300 ease-in-out`,
        isOpen ? 'w-64 sm:w-72' : 'w-0'
      ]"
      class="z-40"
    >
      <div class="p-6">
        <div class="mb-8 text-center">
          <template v-if="orgLogo">
            <div class="mb-14 mt-3 text-center">
              <img
                class="ml-[9px] max-h-16 max-w-[100%] object-cover"
                :src="orgLogo"
                :alt="`${orgName} Logo`"
              />
            </div>
          </template>
          <template v-else>
            <div class="inline-block px-4 py-2 ps-0 text-xl font-bold">
              {{ orgName }}
            </div>
          </template>
        </div>

        <h2
          class="mb-6 text-xl font-semibold"
          :class="`text-${themeColor}-800`"
        >
          Übersicht
        </h2>
        <div
          v-for="section in rawSchema"
          :key="section.uuid"
          class="mb-4"
        >
          <div
            class="flex cursor-pointer items-center justify-between rounded-lg bg-white p-3 shadow-sm transition-shadow
              duration-300 hover:shadow-md"
            @click="toggleSection(section.uuid)"
          >
            <h3
              class="mr-2 truncate text-lg font-semibold"
              :class="`text-${themeColor}-600`"
            >
              {{ truncateText(section.title, 18) }}
            </h3>
            <ChevronDown
              :class="[
                'h-5 w-5 flex-shrink-0 transition-transform duration-300',
                `text-${themeColor}-500`,
                { 'rotate-180 transform': openSections.includes(section.uuid) }
              ]"
            />
          </div>
          <transition name="fade">
            <ul
              v-if="openSections.includes(section.uuid)"
              class="ml-4 mt-2 space-y-2"
            >
              <li
                v-for="item in section.items"
                :key="item.uuid"
                :class="[
                  'flex cursor-pointer items-center rounded-md p-2 transition-colors duration-200',
                  isActiveField(item.uuid)
                    ? `border-l-4 border-${themeColor}-500 bg-${themeColor}-200 text-${themeColor}-700`
                    : `text-gray-700 hover:bg-${themeColor}-50`
                ]"
                @click="handleItemClick(item.uuid)"
              >
                <div class="flex flex-1 items-center">
                  <div
                    v-if="isQuestionAnswered(item.uuid)"
                    class="h-6 w-6 overflow-hidden rounded-lg object-cover text-green-500"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke-width="1.5"
                      stroke="currentColor"
                      class="h-6 w-6"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                      />
                    </svg>
                  </div>
                  <span class="ml-3 truncate text-sm font-medium">
                    {{ truncateText(item.label, 18) }}
                  </span>
                </div>
              </li>
            </ul>
          </transition>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
  import { ChevronDown, ChevronLeft, Menu } from 'lucide-vue-next'
  import { useQuestionnaireStore } from '@/store/questionaireStore'
  import { useThemeColor } from '@/composables/useThemeColor'
  import type { Page } from '@/types'

  const themeColor = computed(() => useThemeColor().value)

  const props = defineProps({
    rawSchema: {
      type: Array as () => Page[],
      required: true
    },
    activeField: {
      type: String,
      required: true
    },
    orgName: {
      type: String,
      required: true
    },
    orgLogo: {
      type: String,
      required: false
    }
  })

  const emit = defineEmits(['jumpToField', 'sidebarToggle'])
  const isOpen = ref(true)
  const openSections = ref<string[]>([])
  const store = useQuestionnaireStore()

  const isLargeScreen = () => window.innerWidth >= 640

  const updateSidebarState = () => {
    isOpen.value = isLargeScreen()
    emit('sidebarToggle', isOpen.value)
  }

  const toggleSidebar = () => {
    isOpen.value = !isOpen.value
    emit('sidebarToggle', isOpen.value)
  }

  const toggleSection = (sectionUuid: string) => {
    if (openSections.value.includes(sectionUuid)) {
      // If the section is already open, close only this section
      openSections.value = openSections.value.filter((uuid) => uuid !== sectionUuid)
    } else {
      // If the section is closed, open this section and close all others
      openSections.value = [sectionUuid]
    }
  }

  const updateOpenSections = () => {
    const activeFieldUuid = props.activeField
    if (activeFieldUuid) {
      const currentSection = props.rawSchema.find((section) =>
        section.items.some((item) => item.uuid === activeFieldUuid)
      )
      if (currentSection) {
        openSections.value = [currentSection.uuid]
      } else {
        openSections.value = props.rawSchema.length > 0 ? [props.rawSchema[0].uuid] : []
      }
    }
  }

  const handleItemClick = (uuid: string) => {
    emit('jumpToField', uuid)
    if (window.innerWidth < 640) {
      isOpen.value = false
      emit('sidebarToggle', false)
    }
  }

  const isQuestionAnswered = (uuid: string) => {
    const value = store.formData[uuid]
    if (typeof value === 'object' && value !== null) {
      // in case of file upload, check if the object is not empty
      return Object.keys(value).length > 0
    }
    return value !== undefined && value !== null && value !== '' && value !== []
  }

  const isActiveField = (uuid: string) => {
    return uuid === props.activeField
  }

  const truncateText = (text: string, maxLength: number) => {
    return text.length > maxLength ? text.slice(0, maxLength) + '...' : text
  }

  onMounted(() => {
    updateSidebarState()
    updateOpenSections()
    window.addEventListener('resize', updateSidebarState)
  })

  onUnmounted(() => {
    window.removeEventListener('resize', updateSidebarState)
  })

  watch(
    () => props.activeField,
    () => {
      updateOpenSections()
      if (window.innerWidth < 640) {
        isOpen.value = false
        emit('sidebarToggle', false)
      }
    }
  )
</script>

<style scoped>
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.3s ease;
  }

  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
  }

  @keyframes float {
    0% {
      box-shadow: 0 5px 15px 0px rgba(0, 0, 0, 0.6);
      transform: translatey(-50%);
    }

    50% {
      box-shadow: 0 25px 15px 0px rgba(0, 0, 0, 0.2);
      transform: translatey(calc(-50% - 10px));
    }

    100% {
      box-shadow: 0 5px 15px 0px rgba(0, 0, 0, 0.6);
      transform: translatey(-50%);
    }
  }

  button {
    animation: float 6s ease-in-out infinite;
  }
</style>
